import { useRef } from 'react';
import XTable from 'components/XTable';
import { Select, Input, DatePicker } from 'antd';
import { getConditionStatisticsList, getDownloadConditionStatisticsApi } from 'api/checkInRecord';
import moment from 'moment';
import { getFilterText, downFile } from 'utils';
import { sexTypes } from 'enumeration/sexTypes';
import { nursingAreas } from 'enumeration/nursingAreas';
import { identityType as identityTypeOptions } from 'enumeration/identityType';
const { Option } = Select;
const { RangePicker } = DatePicker;

// 年龄段选项
const ageGroups = [
    { value: '0-59', text: '0-59岁' },
    { value: '60-69', text: '60-69岁' },
    { value: '70-79', text: '70-79岁' },
    { value: '80-89', text: '80-89岁' },
    { value: '90-99', text: '90-99岁' },
    { value: '100+', text: '100岁以上' },
];

const ConditionStatistics = () => {
    const xTableRef = useRef();

    const queryItems = [
        {
            formItemApis: {
                label: '性别',
                help: null,
                name: 'sex',
            },
            item: (
                <Select style={{ width: 150 }} placeholder="请选择" allowClear>
                    {sexTypes.map(({ value, text }) => {
                        return (
                            <Option key={value} value={value}>
                                {text}
                            </Option>
                        );
                    })}
                </Select>
            ),
        },
        {
            formItemApis: {
                label: '入院时间',
                help: null,
                name: 'checkInDateRange',
            },
            item: (
                <RangePicker
                    style={{ width: 240 }}
                    placeholder={['开始日期', '结束日期']}
                    allowClear
                />
            ),
        },
        {
            formItemApis: {
                label: '离院时间',
                help: null,
                name: 'checkOutDateRange',
            },
            item: (
                <RangePicker
                    style={{ width: 240 }}
                    placeholder={['开始日期', '结束日期']}
                    allowClear
                />
            ),
        },
        {
            formItemApis: {
                label: '年龄段',
                help: null,
                name: 'ageGroup',
            },
            item: (
                <Select style={{ width: 150 }} placeholder="请选择" allowClear>
                    {ageGroups.map(({ value, text }) => {
                        return (
                            <Option key={value} value={value}>
                                {text}
                            </Option>
                        );
                    })}
                </Select>
            ),
        },
        {
            formItemApis: {
                label: '入住区域',
                help: null,
                name: 'nursingArea',
            },
            item: (
                <Select style={{ width: 150 }} placeholder="请选择" allowClear>
                    {nursingAreas.map(({ value, text }) => {
                        return (
                            <Option key={value} value={value}>
                                {text}
                            </Option>
                        );
                    })}
                </Select>
            ),
        },
        {
            formItemApis: {
                label: '身份类型',
                help: null,
                name: 'identityType',
            },
            item: (
                <Select style={{ width: 150 }} placeholder="请选择" allowClear>
                    {identityTypeOptions.map(({ value, text }) => {
                        return (
                            <Option key={value} value={value}>
                                {text}
                            </Option>
                        );
                    })}
                </Select>
            ),
        },
        {
            formItemApis: {
                label: '姓名',
                help: null,
                name: 'name',
            },
            item: <Input style={{ width: 150 }} placeholder="请输入姓名" allowClear />,
        },
    ];

    const columns = [
        {
            title: '姓名',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '身份证号',
            dataIndex: 'idNo',
            key: 'idNo',
        },
        {
            title: '年龄',
            dataIndex: 'age',
            key: 'age',
        },
        {
            title: '性别',
            dataIndex: 'sex',
            key: 'sex',
            render: val => getFilterText(sexTypes, val),
        },
        {
            title: '入住区域',
            dataIndex: 'nursingArea',
            key: 'nursingArea',
            render: val => getFilterText(nursingAreas, val),
        },
        {
            title: '身份类型',
            dataIndex: 'identityType',
            key: 'identityType',
            render: val => getFilterText(identityTypeOptions, val),
        },
        {
            title: '入院时间',
            dataIndex: 'createAt',
            key: 'createAt',
            render: text => {
                return text ? moment(text).format('YYYY-MM-DD') : '';
            },
        },
        {
            title: '离院时间',
            dataIndex: 'updateAt',
            key: 'updateAt',
            render: text => {
                return text ? moment(text).format('YYYY-MM-DD') : '';
            },
        },
        {
            title: '在院状态',
            dataIndex: 'status',
            key: 'status',
            render: text => {
                return text ? '在院' : '已离院';
            },
        },
    ];

    const handelParams = params => {
        const {
            sex,
            nursingArea,
            identityType,
            ageGroup,
            checkInDateRange,
            checkOutDateRange,
            ...otherParams
        } = params;
        const newParams = { ...otherParams };

        // 转换性别枚举值：MALE -> 1, FEMALE -> 2
        if (sex) {
            const sexMap = { MALE: 1, FEMALE: 2 };
            newParams.sex = sexMap[sex];
        }

        // 转换入住区域枚举值：TAKE_CARE_SELF -> 1, SEMI_CARE -> 2, FULL_CARE -> 3
        if (nursingArea) {
            const areaMap = { TAKE_CARE_SELF: 1, SEMI_CARE: 2, FULL_CARE: 3 };
            newParams.nursingArea = areaMap[nursingArea];
        }

        // 转换身份类型枚举值：SELF -> 1, FREE -> 2
        if (identityType) {
            const identityMap = { SELF: 1, FREE: 2 };
            newParams.identityType = identityMap[identityType];
        }

        // 年龄段参数直接传递，不需要转换
        if (ageGroup) {
            newParams.ageGroup = ageGroup;
        }

        // 处理入院时间区间
        if (checkInDateRange && checkInDateRange.length === 2) {
            newParams.checkInStartDate = checkInDateRange[0].format('YYYY-MM-DD');
            newParams.checkInEndDate = checkInDateRange[1].format('YYYY-MM-DD');
        }

        // 处理离院时间区间
        if (checkOutDateRange && checkOutDateRange.length === 2) {
            newParams.checkOutStartDate = checkOutDateRange[0].format('YYYY-MM-DD');
            newParams.checkOutEndDate = checkOutDateRange[1].format('YYYY-MM-DD');
        }

        return newParams;
    };

    const downloadRecord = () => {
        const queryValue = xTableRef.current.queryForm?.getFieldsValue();
        const params = handelParams(queryValue);
        getDownloadConditionStatisticsApi(params).then(res => {
            if (res.success) {
                const filename = `条件统计表${moment().valueOf()}.xls`;
                downFile(res.file, filename);
            }
        });
    };

    const actionBtns = [{ text: '导出', onClick: downloadRecord }];

    return (
        <div>
            <XTable
                columns={columns}
                queryItems={queryItems}
                handelParams={handelParams}
                api={getConditionStatisticsList}
                rowKey="id"
                ref={xTableRef}
                actionBtns={actionBtns}
            />
        </div>
    );
};
export default ConditionStatistics;
